import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useSnakeMarketsFindCurrencies } from '@/lib/endpoints';
import { defaultLobbyPeriod, formatClock } from '@/lib/snake-market';
import { SnakeCurrencyTabs } from './SnakeCurrencyTabs';
import { SnakeLobbyCard, type SnakeLobbyCardMeta } from './SnakeLobbyCard';
import { SnakeLobbyFeedSkeleton } from './SnakeLobbyFeedSkeleton';
import { useNowMs } from './useNowMs';

/** Canonical snake page URL for a currency + resolution. */
function snakeHref(instrument: string, subCategoryCode: string): string {
  return `/snake/${encodeURIComponent(instrument)}/${encodeURIComponent(subCategoryCode)}`;
}

/**
 * The home "Short Markets" snake widget (ARC-84 §4, redesigned per Figma
 * 5751-141388 "Snake In Lobby").
 *
 * Two stacked cards forming one widget: a header card with the "Short
 * Markets" title, the currency pill tabs, and the orange countdown notch
 * (the live window's close, reported up by the card); below it the
 * display-only {@link SnakeLobbyCard} preview.
 * - **Switching currency stays here** — the tab row swaps the previewed market
 *   in place to that instrument's default (5-min) window + its realtime.
 * - **Tapping the card body forwards to the snake page** (§5) with the price to
 *   beat and the full buy options.
 *
 * The card is keyed on the active instrument so a switch remounts it → fresh
 * series fetch + realtime subscription for the new instrument.
 */
export function SnakeLobbyFeed() {
  const navigate = useNavigate();
  const nowMs = useNowMs();
  const { data, isLoading, isError } = useSnakeMarketsFindCurrencies();
  const currencies = data?.currencies ?? [];

  const [activeInstrument, setActiveInstrument] = useState<string | null>(null);
  // Close time + boundary state reported up by the card (it owns the series fetch).
  const [meta, setMeta] = useState<SnakeLobbyCardMeta | null>(null);
  const active = currencies.find((c) => c.instrument === activeInstrument) ?? currencies[0];

  if (isError) {
    return <div className="w-full px-3.5 py-6 text-center text-sm text-muted md:px-0">Failed to load snake markets.</div>;
  }
  if (isLoading) {
    return <SnakeLobbyFeedSkeleton />;
  }
  if (!active) {
    return null;
  }

  function openPage() {
    if (!active) return;
    const period = defaultLobbyPeriod(active.resolutionPeriods);
    if (!period) return;
    navigate(snakeHref(active.instrument, period.subCategoryCode));
  }

  return (
    <div className="w-full">
      {/* Header card — title, currency tabs, countdown notch. */}
      <div className="relative w-full overflow-hidden rounded-t-[12px] border-[1.5px] border-stroke-soft bg-surface py-4 pl-5 pr-4">
        <div className="flex flex-col gap-4">
          <h2 className="m-0 text-[18px] font-semibold capitalize tracking-[-0.36px] text-ink">Short Markets</h2>
          {/* Switch the preview in place (no navigation). */}
          <SnakeCurrencyTabs currencies={currencies} activeInstrument={active.instrument} onChange={setActiveInstrument} />
        </div>
        <div className="absolute right-0 top-4 flex h-[30px] items-center gap-1 rounded-l-pill bg-warning-soft px-2.5">
          <Clock className="size-3.5 text-warning" />
          <span className="text-[18px] font-semibold tracking-[-0.36px] text-warning tabular-nums">
            {meta?.isAdvancing ? 'Next round…' : formatClock(meta?.closeTime ?? null, nowMs)}
          </span>
        </div>
      </div>

      <SnakeLobbyCard key={active.instrument} active={active} onOpen={openPage} onMeta={setMeta} />
    </div>
  );
}
